import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const LOCAL_KEY = "rs_wishlist";

const readLocal = (): string[] => {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeLocal = (ids: string[]) => {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(ids));
  } catch {}
  window.dispatchEvent(new Event("wishlist:changed"));
};

export const useWishlist = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ids, setIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setIds(readLocal());
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("wishlists")
      .select("product_id")
      .eq("user_id", user.id);
    setIds((data || []).map((r: any) => r.product_id));
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener("wishlist:changed", handler);
    return () => window.removeEventListener("wishlist:changed", handler);
  }, [refresh]);

  // Sync local wishlist to DB on login
  useEffect(() => {
    if (!user) return;
    const local = readLocal();
    if (local.length === 0) return;
    (async () => {
      await supabase
        .from("wishlists")
        .upsert(
          local.map((product_id) => ({ user_id: user.id, product_id })),
          { onConflict: "user_id,product_id", ignoreDuplicates: true },
        );
      writeLocal([]);
      refresh();
    })();
  }, [user, refresh]);

  const isWished = useCallback((productId: string) => ids.includes(productId), [ids]);

  const toggle = useCallback(
    async (productId: string, productName?: string) => {
      if (!user) {
        toast.error("Please sign in to use wishlist");
        navigate("/auth");
        return;
      }
      const wished = ids.includes(productId);
      if (wished) {
        setIds((prev) => prev.filter((i) => i !== productId));
        const { error } = await supabase
          .from("wishlists")
          .delete()
          .eq("user_id", user.id)
          .eq("product_id", productId);
        if (error) {
          toast.error("Failed to remove");
          refresh();
        } else {
          toast.success("Removed from wishlist", { description: productName });
          window.dispatchEvent(new Event("wishlist:changed"));
        }
      } else {
        setIds((prev) => [...prev, productId]);
        const { error } = await supabase
          .from("wishlists")
          .insert({ user_id: user.id, product_id: productId });
        if (error && !String(error.message).includes("duplicate")) {
          toast.error("Failed to add");
          refresh();
        } else {
          toast.success("Added to wishlist ❤️", { description: productName });
          window.dispatchEvent(new Event("wishlist:changed"));
        }
      }
    },
    [user, ids, navigate, refresh],
  );

  return { ids, count: ids.length, isWished, toggle, loading, refresh };
};
